import { type Ref } from "vue";
import type {
  SummaryTypeRadioItem,
  ShopSelectValue,
  AreaSelectValue,
} from "../types";
import {
  DownloadDailySalesAllShopByDayDocument,
  DownloadDailySalesAllShopByDayQueryVariables,
  DownloadDailySalesAllShopByDayQuery,
  DownloadDailySalesAllShopByShopDocument,
  DownloadDailySalesAllShopByShopQueryVariables,
  DownloadDailySalesAllShopByShopQuery,
  DownloadDailySalesEachShopDocument,
  DownloadDailySalesEachShopQueryVariables,
  DownloadDailySalesEachShopQuery,
  DailySalesAllShopListCommonInput,
  DailySalesEachListCommonInput,
  OperationInput,
} from "@/generated/graphql";
import { ApolloQueryResult } from "@apollo/client/core";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { saveAs } from "file-saver";
import Encoding from "encoding-japanese";

interface UseSalesSummaryDownloadParams {
  searchConditionDeliveryDateFrom: Ref<string | undefined | null>;
  searchConditionDeliveryDateTo: Ref<string | undefined | null>;
  statusSelectedItem: Ref<string[]>;
  shopSelectValues: Ref<ShopSelectValue>;
  areaSelectValues: Ref<AreaSelectValue>;
  mode: Ref<"each_day" | "each_shop">;
  summeryTypeValues: Ref<SummaryTypeRadioItem>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  overlay: Ref<boolean>;
  downloadingDialog: Ref<boolean>;
  listDataTotalCount: Ref<number>;
  dispAllShopByDaylist: Ref<boolean>;
  dispAllShopByShoplist: Ref<boolean>;
  dispEachShopByDaylist: Ref<boolean>;
}

/**
 * 一覧データのダウンロード処理を提供する composable。
 *
 * @returns ダウンロード実行に必要な関数群
 */
export const useSalesSummaryDownload = (params: UseSalesSummaryDownloadParams) => {
  const {
    searchConditionDeliveryDateFrom,
    searchConditionDeliveryDateTo,
    statusSelectedItem,
    shopSelectValues,
    areaSelectValues,
    mode,
    summeryTypeValues,
    errdialog,
    errMessage,
    overlay,
    downloadingDialog,
    listDataTotalCount,
    dispAllShopByDaylist,
    dispAllShopByShoplist,
    dispEachShopByDaylist,
  } = params;

  const decodeBase64Csv = (base64: string): string => {
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const text = new TextDecoder("utf-8").decode(bytes);
    return text.replace(/^\uFEFF/, "");
  };

  /**
   * ダウンロード @Click
   */
  const onClickDownload = async (): Promise<void> => {
    // 一覧にデータが表示されていない場合はダウンロードしない
    if (!dispAllShopByDaylist.value && !dispAllShopByShoplist.value && !dispEachShopByDaylist.value) {
      return;
    }

    downloadingDialog.value = true;

    try {
      if (mode.value === "each_day") {
        if (summeryTypeValues.value.index === "1") {
          await downloadDailySalesAllShopByDay();
        } else if (summeryTypeValues.value.index === "2") {
          await downloadDailySalesEachShopByDay();
        }
      } else {
        await downloadDailySalesAllShopByShop();
      }
    } finally {
      downloadingDialog.value = false;
    }
  };

  const downloadDailySalesAllShopByDay = async (): Promise<void> => {
    if (dispAllShopByDaylist.value === false) {
      return;
    }
    // graphql接続
    const apolloClient = await generateApolloClient();

    // 日別集計データ（全店舗日付別）ダウンロード  input
    const input: DailySalesAllShopListCommonInput = {
      chain_code: localStorageGetChainCode(),
      customer_code: localStorageGetCustomerCode(),
      delivery_date_from:
        searchConditionDeliveryDateFrom.value !== undefined &&
        searchConditionDeliveryDateFrom.value !== null &&
        searchConditionDeliveryDateFrom.value !== ""
          ? searchConditionDeliveryDateFrom.value
          : undefined,
      delivery_date_to:
        searchConditionDeliveryDateTo.value !== undefined &&
        searchConditionDeliveryDateTo.value !== null &&
        searchConditionDeliveryDateTo.value !== ""
          ? searchConditionDeliveryDateTo.value
          : undefined,
      delivery_status:
        statusSelectedItem.value !== undefined &&
        statusSelectedItem.value !== null &&
        statusSelectedItem.value.length > 0
          ? statusSelectedItem.value
          : undefined,
    };
    // 日別集計データ（全店舗日付別）ダウンロード logInput
    const logInput: OperationInput = {
      area_code: "10",
      detail: "操作詳細",
      display_category: "画面名",
      operation: "操作",
      user_id: localStorageGetUserId(),
    };
    // 日別集計データ（全店舗日付別）ダウンロード [戻り値:CSVはBase64エンコード済み]
    let result: ApolloQueryResult<DownloadDailySalesAllShopByDayQuery>;
    try {
      result = await apolloClient.query<
        DownloadDailySalesAllShopByDayQuery,
        DownloadDailySalesAllShopByDayQueryVariables
      >({
        query: DownloadDailySalesAllShopByDayDocument,
        variables: {
          input: input,
          logInput: logInput,
        },
        fetchPolicy: "no-cache",
      });
    } catch (err: unknown) {
      console.error(err);
      overlay.value = false;
      errdialog.value = true;
      errMessage.value =
        err instanceof Error ? err.message : "日別売り上げのダウンロードに失敗しました。";
      return;
    }

    if (
      result.data.downloadDailySalesAllShopByDay === undefined ||
      result.data.downloadDailySalesAllShopByDay === null
    ) {
      // 日別売り上げダウンロード エラー
      console.error("result.data.downloadTransPickupRequest is undefined or null");
      return;
    }

    // ダウンロード データ処理
    const json: { csv: string } = JSON.parse(result.data.downloadDailySalesAllShopByDay) as {
      csv: string;
    };

    // Base64デコード
    const tempCsvData = decodeBase64Csv(json.csv);

    // Shift-JISにエンコード
    const unicodeArray = Encoding.stringToCode(tempCsvData);
    const sjisArray = Encoding.convert(unicodeArray, {
      to: "SJIS",
      from: "UNICODE",
    });
    const uint8Array = new Uint8Array(sjisArray);

    // ダウンロードファイル作成
    const blob = new Blob([uint8Array], {
      type: "text/csv",
    });

    // ダウンロード
    saveAs(blob, "sales_summary.csv");
  };

  const downloadDailySalesAllShopByShop = async (): Promise<void> => {
    if (dispAllShopByShoplist.value === false) {
      return;
    }
    // graphql接続
    const apolloClient = await generateApolloClient();

    // 日別集計データ（全店舗店舗別）ダウンロード  input
    const input: DailySalesAllShopListCommonInput = {
      chain_code: localStorageGetChainCode(),
      customer_code: localStorageGetCustomerCode(),
      delivery_date_from:
        searchConditionDeliveryDateFrom.value !== undefined &&
        searchConditionDeliveryDateFrom.value !== null &&
        searchConditionDeliveryDateFrom.value !== ""
          ? searchConditionDeliveryDateFrom.value
          : undefined,
      delivery_date_to:
        searchConditionDeliveryDateTo.value !== undefined &&
        searchConditionDeliveryDateTo.value !== null &&
        searchConditionDeliveryDateTo.value !== ""
          ? searchConditionDeliveryDateTo.value
          : undefined,
      delivery_status:
        statusSelectedItem.value !== undefined &&
        statusSelectedItem.value !== null &&
        statusSelectedItem.value.length > 0
          ? statusSelectedItem.value
          : undefined,
    };
    // 日別集計データ（全店舗店舗別）ダウンロード logInput
    const logInput: OperationInput = {
      area_code: "10",
      detail: "操作詳細",
      display_category: "画面名",
      operation: "操作",
      user_id: localStorageGetUserId(),
    };
    // 日別集計データ（全店舗店舗別）ダウンロード [戻り値:CSVはBase64エンコード済み]
    let result: ApolloQueryResult<DownloadDailySalesAllShopByShopQuery>;
    try {
      result = await apolloClient.query<
        DownloadDailySalesAllShopByShopQuery,
        DownloadDailySalesAllShopByShopQueryVariables
      >({
        query: DownloadDailySalesAllShopByShopDocument,
        variables: {
          input: input,
          logInput: logInput,
        },
        fetchPolicy: "no-cache",
      });
    } catch (err: unknown) {
      overlay.value = false;
      errdialog.value = true;
      errMessage.value =
        err instanceof Error ? err.message : "日別売り上げのダウンロードに失敗しました。";
      return;
    }

    if (
      result.data.downloadDailySalesAllShopByShop === undefined ||
      result.data.downloadDailySalesAllShopByShop === null
    ) {
      // 日別売り上げダウンロード エラー
      console.error("result.data.downloadTransPickupRequest is undefined or null");
      return;
    }

    // 在庫移動・回収依頼ダウンロード データ処理
    const json: { csv: string } = JSON.parse(result.data.downloadDailySalesAllShopByShop) as {
      csv: string;
    };

    // Base64デコード
    const tempCsvData = decodeBase64Csv(json.csv);

    // Shift-JISにエンコード
    const unicodeArray = Encoding.stringToCode(tempCsvData);
    const sjisArray = Encoding.convert(unicodeArray, {
      to: "SJIS",
      from: "UNICODE",
    });
    const uint8Array = new Uint8Array(sjisArray);

    // ダウンロードファイル作成
    const blob = new Blob([uint8Array], {
      type: "text/csv",
    });

    // ダウンロード
    saveAs(blob, "sales_summary.csv");
  };

  const downloadDailySalesEachShopByDay = async (): Promise<void> => {
    if (dispEachShopByDaylist.value === false) {
      return;
    }
    // graphql接続
    const apolloClient = await generateApolloClient();

    // 日別集計データ（全店舗店舗別）ダウンロード  input
    const shopCode = shopSelectValues.value?.shop_code ?? "";
    const input: DailySalesEachListCommonInput = {
      area_code:
        areaSelectValues.value.area_code !== undefined &&
        areaSelectValues.value.area_code !== null &&
        areaSelectValues.value.area_code !== ""
          ? areaSelectValues.value.area_code
          : undefined,
      chain_code: localStorageGetChainCode(),
      customer_code: localStorageGetCustomerCode(),
      delivery_date_from:
        searchConditionDeliveryDateFrom.value !== undefined &&
        searchConditionDeliveryDateFrom.value !== null &&
        searchConditionDeliveryDateFrom.value !== ""
          ? searchConditionDeliveryDateFrom.value
          : undefined,
      delivery_date_to:
        searchConditionDeliveryDateTo.value !== undefined &&
        searchConditionDeliveryDateTo.value !== null &&
        searchConditionDeliveryDateTo.value !== ""
          ? searchConditionDeliveryDateTo.value
          : undefined,
      delivery_status:
        statusSelectedItem.value !== undefined &&
        statusSelectedItem.value !== null &&
        statusSelectedItem.value.length > 0
          ? statusSelectedItem.value
          : undefined,
      shop_code: shopCode,
    };

    // 日別集計データ（全店舗店舗別）ダウンロード logInput
    const logInput: OperationInput = {
      area_code: "10",
      detail: "操作詳細",
      display_category: "画面名",
      operation: "操作",
      user_id: localStorageGetUserId(),
    };

    // 日別集計データ（全店舗店舗別）ダウンロード [戻り値:CSVはBase64エンコード済み]
    let result: ApolloQueryResult<DownloadDailySalesEachShopQuery>;
    try {
      result = await apolloClient.query<
        DownloadDailySalesEachShopQuery,
        DownloadDailySalesEachShopQueryVariables
      >({
        query: DownloadDailySalesEachShopDocument,
        variables: {
          input: input,
          logInput: logInput,
        },
        fetchPolicy: "no-cache",
      });
    } catch (err: unknown) {
      overlay.value = false;
      errdialog.value = true;
      errMessage.value =
        err instanceof Error ? err.message : "日別売り上げのダウンロードに失敗しました。";
      return;
    }

    if (
      result.data.downloadDailySalesEachShop === undefined ||
      result.data.downloadDailySalesEachShop === null
    ) {
      // 日別売り上げダウンロード エラー
      console.error("result.data.downloadTransPickupRequest is undefined or null");
      return;
    }

    // 在庫移動・回収依頼ダウンロード データ処理
    const json: { csv: string } = JSON.parse(result.data.downloadDailySalesEachShop) as {
      csv: string;
    };

    // Base64デコード
    const tempCsvData = decodeBase64Csv(json.csv);

    // Shift-JISにエンコード
    const unicodeArray = Encoding.stringToCode(tempCsvData);
    const sjisArray = Encoding.convert(unicodeArray, {
      to: "SJIS",
      from: "UNICODE",
    });
    const uint8Array = new Uint8Array(sjisArray);

    // ダウンロードファイル作成
    const blob = new Blob([uint8Array], {
      type: "text/csv",
    });

    // ダウンロード
    saveAs(blob, "sales_summary.csv");
  };

  return {
    onClickDownload,
  };
};
